import type { AxiosError, AxiosResponse } from 'axios';

import CLocalizedTextStringStatic from './components/CLocalizedTextStringStatic';
import type { CoreAPIResponseSchema } from './models/CoreAPIResponseSchema';

export class CoreJsonAPIResponse<T> {
  /**
   * Core class compatible with the CoreJsonResponse class in the backend.
   */
  constructor(public axiosResponseOrError: AxiosResponse<T> | AxiosError) {}

  private getNetworkErrorString(throwNetworkErrors: boolean = true): string | undefined {
    const responseStatus = (this.axiosResponseOrError as AxiosResponse).status;
    let errorMessage: string | undefined;

    switch (responseStatus) {
      case 200:
        break;
      case 500:
        break;
      case 504:
        errorMessage = CLocalizedTextStringStatic('error.serverDown');
        break;
      default:
        errorMessage = CLocalizedTextStringStatic('globalErrorUnknown');
        break;
    }

    if (throwNetworkErrors && errorMessage) throw new Error(errorMessage);

    return errorMessage;
  }

  /**
   * Currently, we only expect the response type to be either a Blob or application/json.
   * @private
   */
  private async getCoercedResponse(): Promise<CoreAPIResponseSchema<T>> {
    switch (this.axiosResponseOrError.status) {
      case 200:
        if (!((this.axiosResponseOrError as AxiosResponse).data instanceof Blob)) {
          return (this.axiosResponseOrError as AxiosResponse<T>).data as CoreAPIResponseSchema<T>;
        } else {
          /**
           * Augment response for blob.
           */
          return {
            data: (this.axiosResponseOrError as AxiosResponse).data,
            success: true,
            message: ''
          };
        }
      default:
        if (!((this.axiosResponseOrError as AxiosError).response?.data instanceof Blob)) {
          return (this.axiosResponseOrError as AxiosError).response?.data as CoreAPIResponseSchema<T>;
        } else {
          /**
           * Infer JSON response for blob.
           */
          return JSON.parse(
            await ((this.axiosResponseOrError as AxiosError).response?.data as Blob).text()
          ) as CoreAPIResponseSchema<T>;
        }
    }
  }

  public async getData(throwNetworkErrors: boolean = true): Promise<T | undefined> {
    return this.getNetworkErrorString(throwNetworkErrors) ? undefined : (await this.getCoercedResponse()).data;
  }

  public getBlob(throwNetworkErrors: boolean = true): Blob | undefined {
    return this.getNetworkErrorString(throwNetworkErrors)
      ? undefined
      : ((this.axiosResponseOrError as AxiosResponse<T>).data as Blob);
  }

  public async getMessage(throwNetworkErrors: boolean = true): Promise<string> {
    return this.getNetworkErrorString(throwNetworkErrors) ?? (await this.getCoercedResponse()).message;
  }

  public async isSuccess(throwNetworkErrors: boolean = true): Promise<boolean> {
    return this.getNetworkErrorString(throwNetworkErrors) ? false : (await this.getCoercedResponse()).success;
  }

  public async getDetail(throwNetworkErrors: boolean = true): Promise<Record<string, string[]> | undefined> {
    const errorMessage = this.getNetworkErrorString(throwNetworkErrors);
    return errorMessage ? { general: [errorMessage] } : (await this.getCoercedResponse()).detail;
  }
}
